import { useCallback, useEffect, useMemo, useRef } from 'react';

import { bind, type UnbindFn } from 'bind-event-listener';

import { fg } from '@atlaskit/platform-feature-flags/fg';

import { useEntryPointPreload } from './useEntryPointPreload';

const disabledTriggerSelector = ':disabled, [disabled], [aria-disabled="true"]';

export type OnLoad<T> = (obj: { dispose: () => void; reference: T }) => void;

export type UsePressablePreloadRefProps<T> = {
	load: () => T;
	loadOnFocus?: boolean;
	onBeforeLoad?: () => void;
	onLoad: OnLoad<T>;
	preloadOnFocus?: boolean;
};

export function usePressablePreloadRef<T extends { dispose: () => void }>({
	load,
	onLoad,
	onBeforeLoad,
	loadOnFocus = false,
	preloadOnFocus = false,
}: UsePressablePreloadRefProps<T>): (element: HTMLElement | null) => void {
	const nodeRef = useRef<HTMLElement | null>(null);
	const eventListeners = useRef<UnbindFn[]>([]);

	const { preloadReference, cancelPreload, eagerLoad } = useEntryPointPreload(load);

	const clickListener = useMemo(() => {
		return () => {
			if (nodeRef.current?.matches(disabledTriggerSelector)) {
				cancelPreload();
				return;
			}

			if (onBeforeLoad) {
				onBeforeLoad();
			}
			onLoad(eagerLoad());
		};
	}, [cancelPreload, onBeforeLoad, onLoad, eagerLoad]);

	const mouseDownListener = useMemo(() => {
		return () => {
			if (nodeRef.current?.matches(disabledTriggerSelector)) {
				cancelPreload();
				return;
			}

			eagerLoad();
		};
	}, [cancelPreload, eagerLoad]);

	const mouseEnterListener = useMemo(() => {
		return () => {
			if (nodeRef.current?.matches(disabledTriggerSelector)) {
				cancelPreload();
				return;
			}

			preloadReference();
		};
	}, [cancelPreload, preloadReference]);

	const focusListener = useCallback(() => {
		// focus() can emit trusted events when a popup or modal restores focus on close.
		// Only keyboard traversal expresses fresh focus-preload intent in that case.
		if (fg('platform_dst-popup-trigger-dispose-on-close')) {
			return;
		}
		mouseEnterListener();
	}, [mouseEnterListener]);

	const cleanUpEventListeners = useCallback(() => {
		eventListeners.current.forEach((unbind) => {
			unbind();
		});
		eventListeners.current = [];
	}, []);

	const addEventListener = useCallback(
		(element: HTMLElement, type: string, callback: EventListener) => {
			const unbind = bind(element, {
				type,
				listener: callback,
			});

			eventListeners.current.push(unbind);
		},
		[],
	);

	const refCallback = useCallback(
		(element: HTMLElement | null) => {
			if (element !== nodeRef.current) {
				if (nodeRef.current) {
					cancelPreload();
					cleanUpEventListeners();
				}
				if (element) {
					addEventListener(element, 'click', clickListener);
					addEventListener(element, 'mousedown', mouseDownListener);
					addEventListener(element, 'mouseenter', mouseEnterListener);
					addEventListener(element, 'mouseleave', cancelPreload);
					if (loadOnFocus) {
						addEventListener(element, 'focusin', clickListener);
					} else if (preloadOnFocus) {
						addEventListener(element, 'focusin', focusListener);
						addEventListener(element, 'focusout', cancelPreload);
					}
				}
				// Update the ref with the new element
				nodeRef.current = element;
			}
		},
		[
			clickListener,
			mouseDownListener,
			mouseEnterListener,
			focusListener,
			cancelPreload,
			loadOnFocus,
			addEventListener,
			cleanUpEventListeners,
			preloadOnFocus,
		],
	);

	useEffect(() => {
		return () => {
			cleanUpEventListeners();
		};
	}, [cleanUpEventListeners]);

	return refCallback;
}
